"use client";

import { useRecordingMicLevel } from "@/hooks/useRecordingMicLevel";
import type { CSSProperties } from "react";
import styles from "./ImpromptuMicIndicator.module.css";

export function ImpromptuMicIndicator({
  getStream,
  surface,
}: {
  getStream?: () => MediaStream | null;
  surface: "light" | "dark";
}) {
  const { level, silent } = useRecordingMicLevel(getStream);
  const active = level !== null && level >= 0.04 && !silent;
  const muted = surface === "light" ? "text-[#6C6C70]" : "text-[#AEAEB2]";
  const accent = surface === "light" ? "text-[#A8431F]" : "text-[#FD954F]";
  const silenceWarning = "No sound detected. Check that your microphone is on.";
  const label = silent
    ? silenceWarning
    : active
      ? "Sound detected"
      : level === null
        ? "Recording"
        : "Speak when you're ready";

  return (
    <div
      className={`flex max-w-72 items-center gap-3 text-sm ${silent || active ? accent : muted}`}
    >
      <span className="flex h-5 shrink-0 items-center gap-[3px]" aria-hidden="true">
        {[0.5, 0.8, 1, 0.8, 0.5].map((weight, index) => (
          <span
            key={index}
            className={styles.bar}
            style={
              {
                "--mic-scale": active ? 0.2 + Math.sqrt(level!) * 0.8 * weight : 0.2,
              } as CSSProperties
            }
          />
        ))}
      </span>
      <p>{label}</p>
      {/* Keep ordinary sound/pause changes out of the announcement queue. The
          persistent live region only changes when the silence warning changes. */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {silent ? silenceWarning : ""}
      </span>
    </div>
  );
}
